import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, Button, Badge, KV, Field, Chip, Center, Spinner, PageHeader } from "@/components/ui";
import { Topbar } from "@/components/Layout";
import { DatePicker } from "@/components/DatePicker";
import { api } from "@/lib/api";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const LANGS = ["English", "Spanish", "Tagalog", "French", "Creole", "Urdu", "Mandarin", "Russian", "Polish"];

export default function CarePreferences() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const client_id = params.get("client_id");
  const [data, setData] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/office/clients/${client_id}/care-preferences`);
      setData(r);
      const base = r.record || r.prefill;
      setF({ care_categories: [], days: [], cultural_fit: [], home_pets: false, home_stairs: false, home_smoking: false, safety_conversation_completed: false, ...base });
    } catch (e) { setMsg(e.message || "Failed to load"); }
  }, [client_id]);
  useEffect(() => { if (client_id) load(); }, [client_id, load]);

  const toggle = (k, v) => setF((p) => ({ ...p, [k]: (p[k] || []).includes(v) ? p[k].filter((x) => x !== v) : [...(p[k] || []), v] }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f };
      for (const k of ["id", "client_id", "version", "built_by", "built_by_role", "updated_at", "created_at", "care_category_labels", "non_clinical_attestation"]) delete body[k];
      const r = await api.put(`/office/clients/${client_id}/care-preferences`, body);
      setMsg(`Saved — version ${r.record.version}. Portable to any partner agency.`); await load();
    } catch (e) { setMsg(e.message || "Save failed"); } finally { setBusy(false); setTimeout(() => setMsg(null), 4000); }
  };

  if (!data || !f) return <div className="screen"><Topbar title="Care preferences record" /><Center><Spinner /></Center></div>;
  const cats = data.options.care_categories, edit = data.can_edit;

  return (
    <div className="screen" data-testid="care-prefs-screen">
      <Topbar title="Care preferences record" subtitle={data.client.full_name} />
      <div className="container-narrow">
        <PageHeader title="Care preferences record" subtitle={data.client.full_name} onBack={() => nav(-1)} />
        {msg ? <div className="toast" data-testid="prefs-toast">{msg}</div> : null}
        <Card>
          <div className="tag">RN VIDEO-CALL TOOL · NON-CLINICAL ONLY</div>
          <p className="note">Built by the HomecareMatches RN during a general care-needs and safety conversation. Record support categories, schedule, language, cultural/personality fit and home logistics only — never diagnoses. Travels with the family to any agency.</p>
          {data.record ? <KV k="version" v={`v${data.record.version} · ${data.record.built_by} (${data.record.built_by_role}) · ${new Date(data.record.updated_at).toLocaleString()}`} mono /> : <Badge label="not yet built — prefilled from intake" tone="warning" />}
          {!edit ? <Badge label="read-only for your role (RN / GM / support edit)" tone="neutral" /> : null}
        </Card>
        <Card>
          <div className="tag">SUPPORT CATEGORIES</div>
          <div className="chip-wrap">{Object.entries(cats).map(([k, l]) => <Chip key={k} testID={`pref-cat-${k}`} label={l} selected={(f.care_categories || []).includes(k)} onClick={() => edit && toggle("care_categories", k)} />)}</div>
          <div className="tag" style={{ marginTop: 12 }}>SCHEDULE</div>
          <div className="chip-wrap">{DAYS.map((d) => <Chip key={d} testID={`pref-day-${d}`} label={d} selected={(f.days || []).includes(d)} onClick={() => edit && toggle("days", d)} />)}</div>
          <div style={{ height: 8 }} />
          <Field label="Start time" value={f.start_time || ""} onChange={(v) => setF({ ...f, start_time: v })} placeholder="8:00 AM" />
          <Field label="End time" value={f.end_time || ""} onChange={(v) => setF({ ...f, end_time: v })} placeholder="4:00 PM" />
          <Field label="Hours per week" value={f.hours_per_week || ""} onChange={(v) => setF({ ...f, hours_per_week: v })} placeholder="20" />
          <div className="tag">PREFERRED START DATE {f.preferred_start_date ? `· ${f.preferred_start_date}` : ""}</div>
          {edit ? <DatePicker testID="pref-start-date" value={f.preferred_start_date || null} onChange={(v) => setF({ ...f, preferred_start_date: v })} /> : null}
        </Card>
        <Card>
          <div className="tag">LANGUAGE & FIT</div>
          <div className="chip-wrap">{LANGS.map((l) => <Chip key={l} testID={`pref-lang-${l}`} label={l} selected={f.language === l} onClick={() => edit && setF({ ...f, language: l })} />)}</div>
          <div className="tag" style={{ marginTop: 12 }}>CULTURAL FIT</div>
          <div className="chip-wrap">{data.options.cultural_fit.map((c) => <Chip key={c} testID={`pref-fit-${c}`} label={c} selected={(f.cultural_fit || []).includes(c)} onClick={() => edit && toggle("cultural_fit", c)} />)}</div>
          <div className="tag" style={{ marginTop: 12 }}>PERSONALITY FIT</div>
          <div className="chip-wrap">{data.options.working_styles.map((w) => <Chip key={w} testID={`pref-style-${w}`} label={w} selected={f.personality_fit === w} onClick={() => edit && setF({ ...f, personality_fit: w })} />)}</div>
          <Field label="Or describe (short, non-clinical)" value={f.personality_fit || ""} onChange={(v) => setF({ ...f, personality_fit: v.slice(0, 200) })} placeholder="e.g. Enjoys quiet company" />
        </Card>
        <Card>
          <div className="tag">HOME LOGISTICS</div>
          <div className="chip-wrap">
            <Chip testID="pref-pets" label={`Pets: ${f.home_pets ? "yes" : "no"}`} selected={!!f.home_pets} onClick={() => edit && setF({ ...f, home_pets: !f.home_pets })} />
            <Chip testID="pref-stairs" label={`Stairs: ${f.home_stairs ? "yes" : "no"}`} selected={!!f.home_stairs} onClick={() => edit && setF({ ...f, home_stairs: !f.home_stairs })} />
            <Chip testID="pref-smoking" label={`Smoking household: ${f.home_smoking ? "yes" : "no"}`} selected={!!f.home_smoking} onClick={() => edit && setF({ ...f, home_smoking: !f.home_smoking })} />
          </div>
          <div style={{ height: 8 }} />
          <Field label="Parking" value={f.home_parking || ""} onChange={(v) => setF({ ...f, home_parking: v.slice(0, 200) })} placeholder="Street parking, permit needed" />
          <Field label="Others in the home" value={f.others_in_home || ""} onChange={(v) => setF({ ...f, others_in_home: v.slice(0, 200) })} placeholder="Spouse present during the day" />
          <Field label="Entry notes" value={f.entry_notes || ""} onChange={(v) => setF({ ...f, entry_notes: v.slice(0, 200) })} placeholder="Ring twice, side door" />
          <Chip testID="pref-safety-done" label={`Safety conversation completed: ${f.safety_conversation_completed ? "yes" : "no"}`} selected={!!f.safety_conversation_completed} onClick={() => edit && setF({ ...f, safety_conversation_completed: !f.safety_conversation_completed })} />
        </Card>
        {edit ? <Button testID="prefs-save" icon="save" label={data.record ? "Save new version" : "Save record"} loading={busy} onClick={save} /> : null}
      </div>
    </div>
  );
}
